import { PARCEL_FILTER_VALUE_READERS } from "./constants";
import type { ParcelFilters } from "./types";

/** True when at least one filter is set. */
function hasActiveParcelFilters(filters: ParcelFilters): boolean {
	return (Object.keys(filters) as Array<keyof ParcelFilters>).some((key) => {
		const filter = filters[key];

		if (filter.kind === "range") {
			return filter.min !== null || filter.max !== null;
		}

		if (filter.kind === "flag") {
			return filter.value === true;
		}

		return filter.values.length > 0;
	});
}

/** True when the parcel passes every active filter. */
function evaluateParcelFilters(filters: ParcelFilters, props: GeoJSON.GeoJsonProperties): boolean {
	for (const key of Object.keys(filters) as Array<keyof ParcelFilters>) {
		const filter = filters[key];

		switch (filter.kind) {
			case "set": {
				if (filter.values.length === 0) {
					continue;
				}

				const values = PARCEL_FILTER_VALUE_READERS[key](props) as string[];

				if (values.some((value) => filter.values.includes(value)) === false) {
					return false;
				}

				break;
			}

			case "flag": {
				if (filter.value === false) {
					continue;
				}

				if (PARCEL_FILTER_VALUE_READERS[key](props) !== true) {
					return false;
				}

				break;
			}

			case "range": {
				// The filter is off when both ends are null.
				if (filter.min === null && filter.max === null) {
					continue;
				}

				const valueRange = PARCEL_FILTER_VALUE_READERS[key](props) as [number, number] | null;

				// A parcel with no value here, for example outside any POG zone, cannot pass an active filter.
				if (valueRange === null) {
					return false;
				}

				const [valueMin, valueMax] = valueRange;

				// The whole span sits below the filter minimum, so the parcel is out.
				if (filter.min !== null && valueMax < filter.min) {
					return false;
				}

				// The whole span sits above the filter maximum, so the parcel is out.
				if (filter.max !== null && valueMin > filter.max) {
					return false;
				}

				break;
			}
		}
	}

	return true;
}

export { evaluateParcelFilters, hasActiveParcelFilters };
